import { resolveCatalogObjectUrl } from "@/music-catalog/utils/catalog-view";

import type { LibraryProject, LibraryShelf } from "@/cloud/types/types";
import type { ShelfSpine } from "@/shelf/types/types";

/**
 * What the board draws, decided away from the components.
 *
 * The Shelves arrive as an arrangement — racks, and the project ids standing in
 * their slots — while what has to be drawn is a picture and a name per disc.
 * Reconciling the two is arithmetic over the pull this application already
 * holds, so the board never asks the API a second question to draw a rack.
 */

/**
 * A pull reports a removal as a tombstone rather than as an absence, so a
 * removed Shelf arrives in the list and has to be dropped. Drawing one would
 * stand a rack the user took down back on the board.
 *
 * Ordered the way the board is read — down the rows, then across — so it paints
 * the same way on every load. Left in pull order, a rack the account happened
 * to move would swap places in the DOM with its neighbour on the next visit.
 */
export const selectLiveShelves = (shelves: readonly LibraryShelf[]) =>
  shelves
    .filter((shelf) => shelf.deletedAt === null)
    .sort(
      (left, right) =>
        left.boardYMillimeters - right.boardYMillimeters ||
        left.boardXMillimeters - right.boardXMillimeters ||
        left.id.localeCompare(right.id),
    );

/**
 * Which of a rack's slots actually hold a disc.
 *
 * The one rule about what stands where, so counting the discs on a board and
 * drawing them cannot disagree. A slot the rack does not have is dropped —
 * there is nowhere on the boards to draw it — and a slot two items claim keeps
 * the first, because a slot holds one disc and drawing both would stack one
 * case on top of another.
 */
const resolveOccupiedSlots = (shelf: LibraryShelf) => {
  const bySlotIndex = new Map<number, string>();
  for (const item of shelf.items) {
    const isOnTheRack =
      Number.isInteger(item.slotIndex) && item.slotIndex >= 0 && item.slotIndex < shelf.capacity;
    if (isOnTheRack && !bySlotIndex.has(item.slotIndex)) {
      bySlotIndex.set(item.slotIndex, item.desktopProjectId);
    }
  }
  return bySlotIndex;
};

/**
 * The discs standing on one rack, keyed by the slot each stands in.
 *
 * Keyed rather than listed, because that is the question a rack asks: it draws
 * every slot it has and needs to know what — if anything — is in each. Returned
 * as a list, its one caller rebuilt this same map inline on every render, which
 * on a board being panned is once per rack per frame.
 *
 * A disc whose project the Cloud Library no longer holds still stands, marked
 * as the absence it is: a Shelf built on one computer names projects this
 * account may since have removed, and quietly dropping the disc would rearrange
 * a rack somebody else arranged — the gap left behind is indistinguishable from
 * a gap they meant to leave.
 */
export const resolveShelfSpinesBySlotIndex = ({
  projectsById,
  shelf,
}: {
  projectsById: ReadonlyMap<string, LibraryProject>;
  shelf: LibraryShelf;
}): ReadonlyMap<number, ShelfSpine> =>
  new Map(
    [...resolveOccupiedSlots(shelf)].map(([slotIndex, desktopProjectId]): [number, ShelfSpine] => {
      const project = projectsById.get(desktopProjectId);
      if (!project) {
        return [slotIndex, { slotIndex, status: "absent" }];
      }
      return [
        slotIndex,
        {
          desktopProjectId: project.desktopProjectId,
          slotIndex,
          // Read by digest from the head this application already holds, and
          // under the `preview` kind: a spine is a picture of the same project,
          // stored and released with it, so only the digest is new.
          spineImageUrl: project.spineSha256
            ? resolveCatalogObjectUrl({ contentSha256: project.spineSha256, kind: "preview" })
            : undefined,
          status: "shelved",
          title: project.title,
        },
      ];
    }),
  );

/**
 * How many discs are standing on these racks.
 *
 * Counted through the same slot rule the racks are drawn from, so the headline
 * count cannot disagree with what is on screen: a rack naming a disc in a slot
 * it does not have shows one fewer than it claims.
 */
export const countShelvedDiscs = (shelves: readonly LibraryShelf[]) =>
  shelves.reduce((total, shelf) => total + resolveOccupiedSlots(shelf).size, 0);

/** The projects a rack's slots are resolved against, keyed by the id it names. */
export const indexProjectsById = (projects: readonly LibraryProject[]) =>
  new Map(projects.map((project) => [project.desktopProjectId, project]));
